import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_BASE = '/api';

type AmbassadorRow = {
  name: string; slug: string;
  goal: number; raised: number; groupSlug?: string;
  donorsCount: number; progress: number;
  visits: number; conversionRate: number;
};

type GroupRow = {
  name: string; slug: string; goal: number; raised: number;
  imageUrl?: string; imageFit?: string; color?: string;
  donorsCount: number; visits: number; membersCount: number; progress: number;
};

type ManagerData = {
  campaign: { name: string; slug: string; goal: number; imageUrl?: string; endDate?: string };
  orgName: string;
  totals: {
    raised: number; donors: number; visits: number;
    ambassadorRaised: number; directRaised: number;
    ambassadorsCount: number;
  };
  ambassadors: AmbassadorRow[];
  groups?: GroupRow[];
  pie: Array<{ name: string; slug: string; value: number }>;
};

// Visually distinct palette for the pie. Cycled when there are more
// ambassadors than colors.
const PIE_COLORS = [
  '#f59e0b', '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#0891b2',
  '#db2777', '#65a30d', '#ea580c', '#0284c7', '#9333ea', '#059669',
];

type SortKey = 'raised' | 'visits' | 'conversion' | 'progress' | 'donors' | 'name';
type ViewMode = 'cards' | 'table';

// Pie label renderer — positions percentages outside the slice edge so the
// digits never overlap with neighbouring slices. No connector line; labels
// are far enough out (outerRadius + 24) that the association with the slice
// is obvious from the angle alone.
// Slices smaller than 4% are skipped to avoid label crowding.
const renderPctOutside = ({ cx, cy, midAngle, outerRadius, percent }: any) => {
  if (!percent || percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 28;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      fontSize={12}
      fontWeight={700}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {Math.round(percent * 100)}%
    </text>
  );
};

// Build the [headers, ...rows] matrix used by both XLSX and CSV export.
function buildExportRows(ambassadors: AmbassadorRow[]) {
  const header = ['#', 'שם', 'גויס (₪)', 'יעד (₪)', 'התקדמות (%)', 'תורמים', 'צפיות בלינק', 'אחוז המרה (%)'];
  const rows = ambassadors.map((a, i) => [
    i + 1,
    a.name,
    a.raised,
    a.goal,
    a.progress,
    a.donorsCount,
    a.visits,
    a.conversionRate,
  ]);
  return { header, rows };
}

// CSV export: prefix BOM so Excel opens the Hebrew correctly. Wrap any cell
// containing a comma/quote/newline in quotes.
function downloadCsv(filename: string, header: any[], rows: any[][]) {
  const escape = (v: any) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PublicCampaignManagerDashboard: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<ManagerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>('raised');
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios.get(`${API_BASE}/public/campaign-manager-dashboard/${slug}`)
      .then(res => setData(res.data))
      .catch(() => setError('הקמפיין לא נמצא'))
      .finally(() => setLoading(false));
  }, [slug]);

  const sortedAmbassadors = useMemo(() => {
    if (!data) return [];
    const copy = [...data.ambassadors];
    switch (sortBy) {
      case 'raised':      return copy.sort((a, b) => b.raised - a.raised);
      case 'visits':      return copy.sort((a, b) => b.visits - a.visits);
      case 'conversion':  return copy.sort((a, b) => b.conversionRate - a.conversionRate);
      case 'progress':    return copy.sort((a, b) => b.progress - a.progress);
      case 'donors':      return copy.sort((a, b) => b.donorsCount - a.donorsCount);
      case 'name':        return copy.sort((a, b) => a.name.localeCompare(b.name, 'he'));
      default:            return copy;
    }
  }, [data, sortBy]);

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportCsv = () => {
    if (!data) return;
    const { header, rows } = buildExportRows(sortedAmbassadors);
    const fname = `שגרירים-${data.campaign.slug}-${new Date().toISOString().slice(0,10)}.csv`;
    downloadCsv(fname, header, rows);
  };

  const exportXlsx = async () => {
    if (!data) return;
    setExporting(true);
    try {
      // Lazy import so the xlsx bundle (~700kb) isn't paid for by visitors
      // who only view the dashboard.
      const XLSX = await import('xlsx');
      const { header, rows } = buildExportRows(sortedAmbassadors);
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      // Hint Excel to read this sheet right-to-left.
      (ws as any)['!cols'] = [{ wch: 4 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      (wb as any).Workbook = { Views: [{ RTL: true }] };
      XLSX.utils.book_append_sheet(wb, ws, 'שגרירים');
      const fname = `שגרירים-${data.campaign.slug}-${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, fname);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <div dir="rtl" style={containerStyle}>
      <div style={{ textAlign: 'center' }}>
        <div style={spinnerStyle} />
        <p style={{ color: '#6b7280' }}>טוען דאשבורד...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error || !data) return (
    <div dir="rtl" style={containerStyle}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>😔</div>
        <p style={{ fontSize: 18, color: '#374151' }}>{error || 'שגיאה'}</p>
      </div>
    </div>
  );

  const { campaign, orgName, totals } = data;
  const goalPct = campaign.goal > 0 ? Math.min(100, Math.round((totals.raised / campaign.goal) * 100)) : 0;
  const directShare = totals.raised > 0 ? Math.round((totals.directRaised / totals.raised) * 100) : 0;
  const ambassadorShare = 100 - directShare;

  return (
    <div dir="rtl" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #fef3c7 0%, #f8fafc 25%)', fontFamily: "'Heebo', sans-serif", paddingBottom: 40 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');`}</style>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
          borderRadius: 20, padding: '24px 22px', color: '#fff',
          boxShadow: '0 8px 32px rgba(245,158,11,0.28)', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>{orgName}</div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>📊 {campaign.name}</h1>
              <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.9 }}>דאשבורד מנהל קמפיין · {totals.ambassadorsCount} שגרירים</p>
            </div>
            <button
              onClick={copyShareLink}
              style={{
                padding: '8px 14px', borderRadius: 10, border: 'none',
                background: 'rgba(255,255,255,0.2)', color: '#fff',
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                backdropFilter: 'blur(8px)',
              }}
            >
              {copied ? '✅ הועתק!' : '📋 העתק קישור לדאשבורד'}
            </button>
          </div>
        </div>

        {/* Top KPIs */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12, marginBottom: 16,
        }}>
          <KpiCard label='סה"כ גויס' value={`₪${totals.raised.toLocaleString('he-IL')}`} accent="#059669" sub={campaign.goal > 0 ? `מתוך ₪${campaign.goal.toLocaleString('he-IL')} (${goalPct}%)` : undefined} />
          <KpiCard label="תורמים" value={totals.donors.toLocaleString('he-IL')} accent="#2563eb" />
          <KpiCard label="צפיות בלינקים" value={totals.visits.toLocaleString('he-IL')} accent="#7c3aed" />
          <KpiCard label="גויס דרך שגרירים" value={`₪${totals.ambassadorRaised.toLocaleString('he-IL')}`} accent="#f59e0b" sub={`${ambassadorShare}% מהסכום`} />
        </div>

        {/* Progress to campaign goal */}
        {campaign.goal > 0 && (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>🎯 התקדמות יעד הקמפיין</span>
              <span style={{ fontSize: 13, color: '#6b7280' }}>₪{totals.raised.toLocaleString('he-IL')} / ₪{campaign.goal.toLocaleString('he-IL')}</span>
            </div>
            <div style={{ width: '100%', height: 14, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${goalPct}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #f97316)', borderRadius: 999, transition: 'width 0.6s' }} />
            </div>
            <div style={{ textAlign: 'center', marginTop: 6, fontSize: 18, fontWeight: 900, color: '#f59e0b' }}>{goalPct}%</div>
          </div>
        )}

        {/* Pie chart — one slice per ambassador + a distinct gray slice for
            donations that didn't come through any ambassador link. */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>🥧 פילוח תרומות לפי מקור</h3>
          {data.pie.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: 20, fontSize: 13 }}>אין עדיין תרומות להצגה</p>
          ) : (
            // Recharts ResponsiveContainer measures its parent on first
            // paint; if it sees width:-1 (parent not yet laid out) it
            // bails and the pie never renders. Explicit dimensions on the
            // container — not just on a wrapping div — avoid that race.
            // Height is larger than the pie radius so the outside-positioned
            // percent labels and the bottom legend both have room to breathe.
            <ResponsiveContainer width="100%" height={440}>
              <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <Pie
                  data={data.pie}
                  cx="50%"
                  cy="45%"
                  outerRadius={120}
                  dataKey="value"
                  nameKey="name"
                  labelLine={false}
                  label={renderPctOutside}
                  isAnimationActive
                >
                  {data.pie.map((entry, i) => (
                    // The "ללא שגריר" slice gets a neutral gray so it reads
                    // as a category, not as just another ambassador.
                    <Cell
                      key={i}
                      fill={entry.slug === '__direct__' ? '#9ca3af' : PIE_COLORS[i % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(v: any) => `₪${Number(v).toLocaleString('he-IL')}`} />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Ambassador groups — each group's raised is the live sum of its
            members, so this section always agrees with the table below. */}
        {(data.groups || []).length > 0 && (
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>🎯 קבוצות שגרירים</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
              {(data.groups || []).map(g => {
                const color = g.color || '#f59e0b';
                return (
                  <a
                    key={g.slug}
                    href={`/campaign/${data.campaign.slug}/group/${g.slug}/dashboard`}
                    target="_blank" rel="noreferrer"
                    title="לדאשבורד הקבוצה"
                    style={{ border: '1px solid #f3f4f6', borderRadius: 14, overflow: 'hidden', background: '#fff', display: 'block', textDecoration: 'none' }}>
                    {g.imageUrl && <img src={g.imageUrl} alt={g.name} style={{ width: '100%', height: 84, objectFit: (g.imageFit === 'contain' || g.imageFit === 'fill' ? g.imageFit : 'cover') as any, background: g.imageFit === 'contain' ? '#f9fafb' : undefined, display: 'block' }} />}
                    <div style={{ padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>{g.imageUrl ? '' : '🎯 '}{g.name}</span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{g.membersCount} שגרירים</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#6b7280', margin: '6px 0 4px' }}>
                        <b style={{ color: '#059669' }}>₪{g.raised.toLocaleString('he-IL')}</b>
                        {g.goal > 0 && <> מתוך ₪{g.goal.toLocaleString('he-IL')}</>}
                        <span style={{ marginRight: 8, color: '#9ca3af' }}>· {g.donorsCount} תורמים</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: 8, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
                          <div style={{ width: `${g.progress}%`, height: '100%', background: color, transition: 'width 0.5s' }} />
                        </div>
                        <span dir="ltr" style={{ fontSize: 12, fontWeight: 800, color, minWidth: 34, textAlign: 'end' }}>
                          {g.goal > 0 ? `${g.progress}%` : '—'}
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Ambassador table */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ ...sectionTitleStyle, margin: 0 }}>🤝 סטטוס שגרירים</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {/* View mode toggle */}
              <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
                <button
                  onClick={() => setViewMode('cards')}
                  style={{
                    padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700,
                    background: viewMode === 'cards' ? '#fff' : 'transparent',
                    color: viewMode === 'cards' ? '#111827' : '#6b7280',
                    boxShadow: viewMode === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >🪪 כרטיסים</button>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700,
                    background: viewMode === 'table' ? '#fff' : 'transparent',
                    color: viewMode === 'table' ? '#111827' : '#6b7280',
                    boxShadow: viewMode === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >📋 טבלה</button>
              </div>

              <span style={{ fontSize: 12, color: '#6b7280' }}>מיון:</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortKey)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, background: '#fff' }}
              >
                <option value="raised">גיוס</option>
                <option value="visits">צפיות</option>
                <option value="conversion">המרה</option>
                <option value="progress">% התקדמות</option>
                <option value="donors">תורמים</option>
                <option value="name">שם</option>
              </select>

              {/* Export */}
              <button
                onClick={exportCsv}
                disabled={sortedAmbassadors.length === 0}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}
                title="ייצא לקובץ CSV"
              >📄 CSV</button>
              <button
                onClick={exportXlsx}
                disabled={sortedAmbassadors.length === 0 || exporting}
                style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: '#16a34a', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#fff' }}
                title="ייצא ל-Excel"
              >{exporting ? '⏳' : '📊'} Excel</button>
            </div>
          </div>

          {sortedAmbassadors.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: 24, fontSize: 13 }}>אין שגרירים בקמפיין</p>
          ) : viewMode === 'cards' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sortedAmbassadors.map((a, idx) => (
                <AmbassadorRowCard
                  key={a.slug}
                  rank={idx + 1}
                  amb={a}
                  campaignSlug={campaign.slug}
                />
              ))}
            </div>
          ) : (
            <AmbassadorTable
              rows={sortedAmbassadors}
              campaignSlug={campaign.slug}
              sortBy={sortBy}
              onSort={setSortBy}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '24px 0 8px', color: '#9ca3af', fontSize: 11 }}>
          <p>מופעל ע"י <strong style={{ color: '#6b7280' }}>Amutech</strong></p>
        </div>
      </div>
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; accent: string; sub?: string }> = ({ label, value, accent, sub }) => (
  <div style={{ background: '#fff', borderRadius: 14, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{sub}</div>}
  </div>
);

const AmbassadorRowCard: React.FC<{ rank: number; amb: AmbassadorRow; campaignSlug: string }> = ({ rank, amb, campaignSlug }) => {
  const rankColor = rank === 1 ? '#f59e0b' : rank === 2 ? '#9ca3af' : rank === 3 ? '#b45309' : '#e5e7eb';
  const rankText = rank < 4 ? '#fff' : '#374151';
  const dashUrl = `/campaign/${campaignSlug}/ambassador/${amb.slug}/dashboard`;
  return (
    <div style={{
      background: '#fafafa', borderRadius: 12, padding: '12px 14px',
      border: '1px solid #f3f4f6',
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: rankColor, color: rankText,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 13, flexShrink: 0,
      }}>{rank}</div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <a href={dashUrl} target="_blank" rel="noreferrer" style={{ fontSize: 15, fontWeight: 700, color: '#111827', textDecoration: 'none' }}>
            {amb.name}
          </a>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#059669', whiteSpace: 'nowrap' }}>
            ₪{amb.raised.toLocaleString('he-IL')}
            {amb.goal > 0 && <span style={{ color: '#9ca3af', fontWeight: 500, fontSize: 11 }}> / ₪{amb.goal.toLocaleString('he-IL')}</span>}
          </div>
        </div>
        {/* Progress bar */}
        {amb.goal > 0 && (
          <div style={{ height: 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden', marginTop: 6 }}>
            <div style={{ height: '100%', width: `${amb.progress}%`, background: '#f59e0b', transition: 'width 0.5s' }} />
          </div>
        )}
        {/* Metric chips */}
        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', fontSize: 11, color: '#6b7280' }}>
          <span>🎯 <b style={{ color: '#374151' }}>{amb.progress}%</b></span>
          <span>👥 <b style={{ color: '#374151' }}>{amb.donorsCount}</b> תורמים</span>
          <span>👁️ <b style={{ color: '#374151' }}>{amb.visits.toLocaleString('he-IL')}</b> צפיות</span>
          <span>⚡ <b style={{ color: '#374151' }}>{amb.conversionRate}%</b> המרה</span>
        </div>
      </div>

      <a
        href={dashUrl}
        target="_blank"
        rel="noreferrer"
        title="פתח דאשבורד שגריר"
        style={{
          padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb',
          background: '#fff', color: '#6b7280', fontSize: 11, fontWeight: 600,
          textDecoration: 'none', whiteSpace: 'nowrap',
        }}
      >פתח ↗</a>
    </div>
  );
};

// Compact tabular view of the ambassador list. Column headers double as
// sort triggers — the column matching the current `sortBy` is highlighted.
const AmbassadorTable: React.FC<{
  rows: AmbassadorRow[]; campaignSlug: string;
  sortBy: SortKey; onSort: (k: SortKey) => void;
}> = ({ rows, campaignSlug, sortBy, onSort }) => {
  const cols: Array<{ key: SortKey | null; label: string; align?: 'start' | 'end' | 'center' }> = [
    { key: null,         label: '#', align: 'center' },
    { key: 'name',       label: 'שם' },
    { key: 'raised',     label: 'גויס', align: 'end' },
    { key: null,         label: 'יעד', align: 'end' },
    { key: 'progress',   label: '%', align: 'end' },
    { key: 'donors',     label: 'תורמים', align: 'end' },
    { key: 'visits',     label: 'צפיות', align: 'end' },
    { key: 'conversion', label: 'המרה', align: 'end' },
    { key: null,         label: '', align: 'center' },
  ];

  return (
    <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #f3f4f6' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 640 }}>
        <thead style={{ background: '#fafafa' }}>
          <tr>
            {cols.map((c, i) => (
              <th
                key={i}
                onClick={() => c.key && onSort(c.key)}
                style={{
                  padding: '10px 8px',
                  textAlign: (c.align as any) || 'start',
                  fontWeight: 700,
                  fontSize: 12,
                  color: c.key === sortBy ? '#f59e0b' : '#6b7280',
                  cursor: c.key ? 'pointer' : 'default',
                  borderBottom: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                {c.label}{c.key && sortBy === c.key ? ' ▾' : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => (
            <tr key={a.slug} style={{ borderBottom: i === rows.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
              <td style={tdCenter}>{i + 1}</td>
              <td style={{ ...td, fontWeight: 600 }}>
                <a
                  href={`/campaign/${campaignSlug}/ambassador/${a.slug}/dashboard`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#111827', textDecoration: 'none' }}
                >{a.name}</a>
              </td>
              <td style={{ ...tdEnd, color: '#059669', fontWeight: 700 }}>₪{a.raised.toLocaleString('he-IL')}</td>
              <td style={tdEnd}>{a.goal > 0 ? `₪${a.goal.toLocaleString('he-IL')}` : '—'}</td>
              <td style={tdEnd}>{a.progress}%</td>
              <td style={tdEnd}>{a.donorsCount.toLocaleString('he-IL')}</td>
              <td style={tdEnd}>{a.visits.toLocaleString('he-IL')}</td>
              <td style={tdEnd}>{a.conversionRate}%</td>
              <td style={tdCenter}>
                <a
                  href={`/campaign/${campaignSlug}/ambassador/${a.slug}/dashboard`}
                  target="_blank"
                  rel="noreferrer"
                  title="פתח דאשבורד שגריר"
                  style={{ fontSize: 12, color: '#7c3aed', textDecoration: 'none' }}
                >📊</a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const td: React.CSSProperties = { padding: '10px 8px', color: '#374151' };
const tdEnd: React.CSSProperties = { ...td, textAlign: 'end' };
const tdCenter: React.CSSProperties = { ...td, textAlign: 'center', color: '#9ca3af', fontSize: 12 };

const containerStyle: React.CSSProperties = {
  minHeight: '100vh', background: '#f8fafc',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: "'Heebo', sans-serif",
};

const spinnerStyle: React.CSSProperties = {
  width: 48, height: 48, border: '4px solid #e5e7eb',
  borderTop: '4px solid #f59e0b', borderRadius: '50%',
  animation: 'spin 1s linear infinite', margin: '0 auto 16px',
};

const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 14, padding: 18,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6',
  marginBottom: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 16, fontWeight: 800, color: '#111827', margin: '0 0 10px',
};

export default PublicCampaignManagerDashboard;
